import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../Button';

// ---------------------------------------------------------------------------
// NudgeCard — the one visual language for every proactive message in the new
// look (coach marks, offer toasts, reorder / reward nudges). Same surface,
// icon tile, type scale and button pair everywhere, so a member learns the
// pattern once; only WHERE it sits changes:
//
//   inline    — part of the page flow (a card above the box builder). Grows
//               into place and collapses away (animated height via
//               grid-template-rows) so the page never jumps when one card
//               hands the slot to the next.
//   floating  — bottom-centred toast over the page
//   anchored  — small bubble with a tail, pinned near the thing it explains;
//               the caller positions it via className/style
//
// Pair with useMessageSlot (services/messageQueue) so cards take turns, and
// useNudgeExit (below) to play the exit before unmounting.
// ---------------------------------------------------------------------------

export const NUDGE_EXIT_MS = 300;

const MOTION = 'duration-300 ease-out motion-reduce:transition-none';
const SURFACE = `rounded-2xl bg-white ring-1 ring-black/5 transition-all ${MOTION}`;
const SHADOW = 'shadow-[0_12px_40px_rgba(84,73,71,0.25)]';

const LAYOUT = {
  inline: `grid transition-[grid-template-rows,opacity] ${MOTION}`,
  floating: `${SURFACE} ${SHADOW} fixed bottom-[140px] left-1/2 z-[9999] w-[calc(100%-32px)] max-w-[460px] -translate-x-1/2 p-3.5`,
  anchored: `${SURFACE} ${SHADOW} z-50 w-64 max-w-[calc(100vw-2rem)] p-4 text-left`,
};

const ENTER_FROM = {
  inline: 'grid-rows-[0fr]',
  floating: 'translate-y-5',
  anchoredDown: 'translate-y-2',
  anchoredUp: '-translate-y-1',
};

/**
 * Exit choreography for a card that hides itself (Not now, done, etc.).
 * `startExit()` flips `leaving` so the caller can pass `visible={!leaving}`
 * (plays the collapse) and drop `ready` on its message slot (hands the slot
 * to the next card at once, so that one grows in while this one shrinks);
 * `onExited` fires once the animation is over and it's safe to unmount.
 */
export function useNudgeExit(onExited) {
  const [leaving, setLeaving] = useState(false);
  const timerRef = useRef(null);
  const onExitedRef = useRef(onExited);
  onExitedRef.current = onExited;

  const startExit = useCallback(() => {
    if (timerRef.current) return;
    setLeaving(true);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setLeaving(false);
      onExitedRef.current?.();
    }, NUDGE_EXIT_MS);
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return { leaving, startExit };
}

const CloseIcon = () => (
  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
    <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const NudgeCard = ({
  layout = 'inline',
  visible = true,
  eyebrow,
  title,
  description,
  icon = null,
  imageUrl = '',
  imageAlt = '',
  primaryLabel,
  onPrimary,
  primaryLoading = false,
  primaryDisabled = false,
  secondaryLabel = 'Not now',
  onSecondary,
  actions,
  onClose,
  closeLabel = 'Dismiss',
  tail = 'down',
  tailClassName = 'right-9',
  className = '',
  style,
}) => {
  // Every card eases in on mount; callers flip `visible` off to play the
  // exit before unmounting (see useNudgeExit).
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  const isVisible = entered && visible;

  const isInline = layout === 'inline';
  const isAnchored = layout === 'anchored';
  const enterFrom = isAnchored
    ? (tail === 'up' ? ENTER_FROM.anchoredUp : ENTER_FROM.anchoredDown)
    : ENTER_FROM[layout];

  const hasMedia = Boolean(icon || imageUrl);
  const hasActions = Boolean(actions || primaryLabel || secondaryLabel);

  const renderActions = () =>
    actions || (
      <>
        {secondaryLabel && (
          <Button variant="tertiary" size="sm" pill onClick={onSecondary}>
            {secondaryLabel}
          </Button>
        )}
        {primaryLabel && (
          <Button variant="primary" size="sm" pill onClick={onPrimary} loading={primaryLoading} disabled={primaryDisabled}>
            {primaryLabel}
          </Button>
        )}
      </>
    );

  const body = (
    <>
      {isAnchored && (
        <span
          aria-hidden="true"
          className={`absolute h-3 w-3 rotate-45 bg-white ${tail === 'up' ? '-top-1.5' : '-bottom-1.5'} ${tailClassName}`}
        />
      )}

      <div className={isInline ? 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between' : ''}>
        <div className="flex min-w-0 items-start gap-3">
          {imageUrl ? (
            <img src={imageUrl} alt={imageAlt} className="h-12 w-12 flex-none rounded-xl object-cover ring-1 ring-black/5" />
          ) : icon ? (
            <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-[#fcf1e7] text-[#544947] [&>svg]:h-6 [&>svg]:w-6">
              {icon}
            </span>
          ) : null}
          <div className={`min-w-0 flex-1 ${hasMedia ? 'pt-0.5' : ''}`}>
            {eyebrow && (
              <p className="mb-0.5 truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">{eyebrow}</p>
            )}
            <p className="text-sm font-semibold leading-snug text-[#544947] [text-wrap:balance]">{title}</p>
            {description && <p className="mt-0.5 text-[13px] leading-snug text-[#544947]/75">{description}</p>}
          </div>
        </div>

        {hasActions && (
          <div className={`flex flex-none items-center justify-end gap-2 ${isInline ? `sm:pl-4 ${hasMedia ? 'pl-[60px]' : ''}` : 'mt-3'}`}>
            {renderActions()}
          </div>
        )}
      </div>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white text-gray-500 shadow-sm ring-1 ring-black/10 transition-colors hover:text-[#544947] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
        >
          <CloseIcon />
        </button>
      )}
    </>
  );

  if (isInline) {
    // Height animates through the 0fr ↔ 1fr row; the inner wrapper clips it.
    // The bottom spacing lives inside so it collapses with the card.
    return (
      <div
        role="status"
        className={`${LAYOUT.inline} ${
          isVisible ? 'grid-rows-[1fr] opacity-100' : `pointer-events-none opacity-0 ${ENTER_FROM.inline}`
        } ${className}`}
        style={style}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="pb-4">
            <div className="relative rounded-2xl border border-black/5 bg-white px-4 py-4 sm:px-5">{body}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      className={`${LAYOUT[layout]} ${
        isVisible ? 'translate-y-0 opacity-100' : `pointer-events-none opacity-0 ${enterFrom}`
      } ${className}`}
      style={style}
    >
      {body}
    </div>
  );
};

export default NudgeCard;
